import { useState } from 'react';
import { useCustom, useTranslate } from '@refinedev/core';
import { Card, Empty, Flex, Select, Spin, Typography, theme } from 'antd';
import { Heatmap } from '@ant-design/charts';
import { ProductKind, type HeatmapOut } from '@/api/generated/schemas';
import { BOTTOM_LEGEND } from '@/pages/dashboard/chartLegend';
import { StatsRangePicker } from './StatsRangePicker';
import {
  DEFAULT_DAYS,
  daysBack,
  rangeQuery,
  type StatsRange,
} from './statsRange';

// Row order of the backend matrix: row 0 is Monday.
const WEEKDAYS = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Nd'];

const pad = (h: number) => String(h).padStart(2, '0');

const HOURS = Array.from({ length: 24 }, (_, h) => pad(h));

type Cell = { weekday: string; hour: string; h: number; value: number };

export const ListeningHeatmapCard = () => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const [productKind, setProductKind] = useState<ProductKind>();
  const [range, setRange] = useState<StatsRange>(() => daysBack(DEFAULT_DAYS));

  const { query } = useCustom<HeatmapOut>({
    url: 'progress/segments/stats/heatmap',
    method: 'get',
    config: {
      query: { ...rangeQuery(range), product_kind: productKind },
    },
  });

  const heatmap = query.data?.data;
  const peak = heatmap?.peak;

  const cells: Cell[] = (heatmap?.matrix ?? []).flatMap((row, w) =>
    row.map((value, h) => ({
      weekday: WEEKDAYS[w] ?? '',
      hour: pad(h),
      h,
      value,
    }))
  );

  return (
    <Card
      title="Heatmapa odsłuchu"
      extra={
        <Flex gap="small" wrap>
          <Select<ProductKind>
            allowClear
            placeholder="Wszystkie formaty"
            options={Object.values(ProductKind).map((k) => ({
              value: k,
              label: t(`progress.sessions.format.${k}`),
            }))}
            value={productKind}
            onChange={setProductKind}
            style={{ width: 160 }}
          />
          <StatsRangePicker value={range} onChange={setRange} />
        </Flex>
      }
    >
      <Spin spinning={query.isFetching}>
        {heatmap && !peak ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Brak odsłuchów w wybranym okresie"
          />
        ) : (
          <>
            <Heatmap
              data={cells}
              mark="cell"
              xField="hour"
              yField="weekday"
              colorField="value"
              height={260}
              scale={{
                x: { domain: HOURS },
                y: { domain: WEEKDAYS },
                // One hue, light → dark; the dark theme brings its own ramp.
                color: { type: 'linear', range: [token.blue1, token.blue8] },
              }}
              style={{ inset: 1 }}
              axis={{
                x: {
                  labelFormatter: (v: string) =>
                    Number(v) % 3 === 0 ? `${v}:00` : '',
                },
              }}
              legend={BOTTOM_LEGEND}
              tooltip={{
                title: (d: Cell) =>
                  `${d.weekday}, ${d.hour}:00–${pad(d.h + 1)}:00`,
                items: [
                  (d: Cell) => ({
                    name: 'Segmenty',
                    value: d.value.toLocaleString('pl-PL'),
                  }),
                ],
              }}
            />
            {peak && heatmap && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Szczyt: {WEEKDAYS[peak.weekday]} {pad(peak.hour)}:00 ·{' '}
                {peak.value.toLocaleString('pl-PL')} segmentów · strefa{' '}
                {heatmap.timezone}
              </Typography.Text>
            )}
          </>
        )}
      </Spin>
    </Card>
  );
};
